import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { XCircle } from "lucide-react";
import { apiClient } from "../../utils/apiClient";

export const VerifyMagicLinkPage: React.FC = () => {
  const { verificationToken } = useParams<{ verificationToken: string }>();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent double execution from React StrictMode
    if (hasVerified.current) return;
    hasVerified.current = true;
    verifyToken();
  }, [verificationToken]);

  const verifyToken = async () => {
    if (!verificationToken) {
      setError("Verification failed");
      return;
    }
    try {
      const response = await apiClient.confirmShareLink(verificationToken);
      if (!response.success) {
        setError("Verification failed");
        setVerifying(false);
        return;
      }
      // Redirect to shared resource page (no email in URL)
      // The backend has already marked the share link as verified
      // SharedResourcePage will check isVerified and render the resource
      const shareLinkToken = response.shareLinkToken;
      navigate(`/share/${shareLinkToken}`);
    } catch (err: unknown) {
      setError("Invalid or expired verification link");
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-600 border-t-transparent mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Verifying Your Email
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we verify your email address...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-6">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Verification Failed
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p>Possible reasons:</p>
              <ul className="list-disc list-inside mt-2 text-left">
                <li>The verification link has expired (15 minute limit)</li>
                <li>The link has already been used</li>
                <li>The link is invalid or malformed</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
